import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useAppActions, useAppState } from '@afosto/app-components-react/AppProvider';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ItemsAllButtonSettingsForm from './ItemsAllButtonSettingsForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import Switch from '@afosto/app-components-react/Switch';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import { boolean as booleanFormatter } from '@afosto/app-components-react/DataGrid/formatters';
import translations from './translations';

const GeneralAppSettings = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [showEditPickAllSettings, setShowEditPickAllSettings] = useState(false);
  const { appKey, settings } = useAppState();
  const [actions] = useAppActions();
  const generalSettings = settings?.general ?? {};
  const {
    items_all_button: itemsAllButtonSettings = {},
    show_position_suggestions: showPositionSuggestions = true,
  } = generalSettings;

  const handleUpdateItemsAllButtonSettings = async updatedSettings => {
    try {
      const newSettings = {
        ...generalSettings,
        items_all_button: {
          at_least_one_product_manually: updatedSettings?.atLeastOneProductManually ?? true,
          min_requested_quantity: updatedSettings?.minRequestedQuantity
            ? parseInt(updatedSettings?.minRequestedQuantity, 10)
            : 5,
        },
      };
      await profileApi.updateProfile(
        `afosto.tenant.${appKey}.shipments.v1.settings.general`,
        newSettings,
      );

      actions.setSettings({ ...settings, general: newSettings });

      enqueueSnackbar(intl.formatMessage(translations.feedback.settingsUpdated), {
        variant: 'success',
      });

      setShowEditPickAllSettings(false);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.settingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  const handleUpdateSetting = key => async (ev, newValue) => {
    try {
      if (newValue === settings?.[key]) {
        return;
      }

      await profileApi.updateProfile(`afosto.tenant.${appKey}.shipments.v1.settings.general`, {
        ...generalSettings,
        [key]: newValue,
      });

      actions.setSettings({ ...settings, general: { ...generalSettings, [key]: newValue } });

      enqueueSnackbar(intl.formatMessage(translations.feedback.settingsUpdated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.settingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <Divider />
        <Box display="flex" p={2} pl={0}>
          <List dense disablePadding style={{ flex: 1 }}>
            <Box pl={2} py={1}>
              <Typography component="div" variant="subtitle2" color="textSecondary">
                {intl.formatMessage(translations.settings.showItemsAllButton)}
              </Typography>
            </Box>
            <ListItem>
              <ListItemText
                primary={intl.formatMessage(translations.settings.minRequestedQuantity)}
                secondary={itemsAllButtonSettings?.min_requested_quantity}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={intl.formatMessage(translations.settings.atLeastOneProductManually)}
                secondary={booleanFormatter(itemsAllButtonSettings?.at_least_one_product_manually)}
              />
            </ListItem>
          </List>
          <Box ml={1}>
            <Button onClick={() => setShowEditPickAllSettings(true)} variant="outlined">
              <FormattedMessage {...translations.actions.edit} />
            </Button>
          </Box>
        </Box>
        <Divider />
        <List dense>
          <ListItem>
            <ListItemText
              primary={intl.formatMessage(translations.settings.showPositionSuggestions)}
            />
            <Box ml={1}>
              <Switch
                name="showPositionSuggestions"
                color="primary"
                checked={showPositionSuggestions}
                onChange={handleUpdateSetting('show_position_suggestions')}
                changeOnMounted={false}
              />
            </Box>
          </ListItem>
        </List>
      </Card>
      <FormDrawer
        isOpen={showEditPickAllSettings}
        onClose={() => setShowEditPickAllSettings(false)}
        title={intl.formatMessage(translations.editItemsAllButtonSettings.title)}
        drawerWidth="400px"
      >
        <ItemsAllButtonSettingsForm
          onCancel={() => setShowEditPickAllSettings(false)}
          onSubmit={handleUpdateItemsAllButtonSettings}
          settings={generalSettings?.items_all_button ?? {}}
        />
      </FormDrawer>
    </>
  );
};

export default GeneralAppSettings;
